import { Button, Checkbox, Stack, Table, TableColumns } from '@sixfold/common-ui';
import * as React from 'react';
import { ReactElement } from 'react';
import JsonView from 'react18-json-view';
import 'react18-json-view/src/style.css';

import styles from '../css/bigquery.module.css';

interface BigqueryItem<T extends object> {
  firstRow: string | null;
  data: T;
  secondRow?: string;
}

type BigqueryContentRow = {
  id: string;
  content: React.ReactNode;
};

export function BigqueryMessage<T extends object>({
  payload,
  compare,
  setCompare,
  index,
  isJustOne,
  checkBoxLabel,
}: {
  payload: BigqueryItem<T>;
  compare: number[];
  setCompare: (items: number[]) => void;
  index: number;
  isJustOne: boolean;
  checkBoxLabel: (item: BigqueryItem<T>) => ReactElement;
}) {
  const [showsPayload, setShowsPayload] = React.useState<boolean>(isJustOne);
  const [collapsed, setCollapsed] = React.useState<boolean>(false);
  const isDisabled = isJustOne || (compare.length === 2 && !compare.includes(index));

  const headerContent = (
    <div className={styles.bigquery__message__header}>
      <div className={styles.bigquery__message__header__checkbox}>
        <Checkbox
          label=""
          hideLabel
          checked={compare.includes(index)}
          disabled={isDisabled}
          onChange={({ checked }) => {
            if (checked) {
              setCompare([...compare, index].sort());
            } else {
              setCompare(compare.filter((item) => item !== index));
            }
          }}
        />
        <div style={isDisabled ? { color: 'gray' } : {}}>{checkBoxLabel(payload)}</div>
      </div>
      <Stack gap="small">
        {showsPayload && (
          <Button size="small" onClick={() => setCollapsed(!collapsed)}>
            {collapsed ? 'Expand' : 'Collapse'}
          </Button>
        )}
        <Button size="small" onClick={() => setShowsPayload(!showsPayload)}>
          {showsPayload ? 'Hide' : 'Show'}
        </Button>
      </Stack>
    </div>
  );

  const columns: TableColumns<BigqueryContentRow> = [
    {
      id: 'content',
      title: headerContent,
      cellContent: ({ value }) => value,
    },
  ];

  const data: BigqueryContentRow[] = showsPayload
    ? [
        {
          id: 'payload',
          content: (
            <div className={styles.bigquery__message__cell}>
              <JsonView
                key={collapsed ? 'collapsed' : 'expanded'}
                src={payload.data}
                collapsed={collapsed ? 1 : false}
                enableClipboard={true}
              />
            </div>
          ),
        },
      ]
    : [];

  return <Table data={data} columns={columns} />;
}
